import { useEffect, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { PostHogProvider as PostHogProviderReact } from '@posthog/react';
import { useGetHome } from 'hooks/sms-hooks';
import { getAuthSession } from 'auth/session';
import { APP_VERSION } from 'constants/constants';
import posthog from './posthog-client';

const projectToken = import.meta.env.VITE_POSTHOG_KEY;

export type PostHogProviderProps = {
  children?: ReactNode;
};

/**
 * Mutable guard state threaded across runPostHogSync calls so we (a) only reset() on a real
 * logout transition and (b) identify once per user. Held in a ref by the provider.
 */
export type PostHogSyncState = {
  wasAuthenticated: boolean;
  identifiedId: string | undefined;
};

/**
 * Effect logic extracted so we can unit-test identify/reset behavior without relying on useEffect
 * flush in the test env. Identify is called once per user with distinctId and properties
 * (codebase, user_type, cpa_id, app_version, etc.); reset is called ONLY on a real logout
 * transition (authenticated → unauthenticated), never on initial/other unauthenticated renders —
 * resetting then would mint a fresh anonymous id mid-funnel and orphan pre-login events like
 * intake_started (LISD-2503). No-op when token is unset. Returns the next guard state.
 */
export function runPostHogSync(
  token: string | undefined,
  isAuthenticated: boolean,
  homeData: { data?: { current_cpa_user_uuid: string; cpa_uuid: string; userpilot_data?: { user_role?: string; firm_name?: string } } } | undefined,
  client: { identify: (id: string, props: Record<string, unknown>) => void; reset: () => void } | null,
  prevState: PostHogSyncState
): PostHogSyncState {
  if (!token) return prevState;
  const next: PostHogSyncState = { ...prevState };
  if (isAuthenticated && homeData?.data) {
    const { current_cpa_user_uuid, cpa_uuid, userpilot_data } = homeData.data;
    // Identify once per user; repeated homeData updates shouldn't re-identify (avoids id churn).
    if (prevState.identifiedId !== current_cpa_user_uuid) {
      const userRole = userpilot_data?.user_role;
      const userType = userRole === 'client' ? 'client' : 'firm';
      client?.identify(current_cpa_user_uuid, {
        codebase: 'liscio-refactor',
        user_type: userType,
        cpa_id: cpa_uuid,
        firm_name: userpilot_data?.firm_name,
        app_version: APP_VERSION,
        platform: 'web',
      });
      next.identifiedId = current_cpa_user_uuid;
    }
  } else if (prevState.wasAuthenticated && !isAuthenticated) {
    client?.reset();
    next.identifiedId = undefined;
  }
  next.wasAuthenticated = isAuthenticated;
  return next;
}

/**
 * PostHog Product Analytics provider (LISD-2217).
 * liscio-refactor: codebase 'liscio-refactor', user_type from home userpilot_data.user_role.
 * Identifies when logged in, resets on logout. SPA pageviews captured via defaults: '2026-01-30'.
 */
export const PostHogProvider = ({ children }: PostHogProviderProps): ReactElement | null => {
  const authSession = getAuthSession();
  const isAuthenticated = !!authSession;
  const { data: homeData } = useGetHome(isAuthenticated);

  // Guard state persists across renders so reset() only fires on a real logout transition and
  // identify() runs once per user (see runPostHogSync). Seeded with the first-render auth state so
  // an initial unauthenticated mount is not treated as a logout.
  const syncStateRef = useRef<PostHogSyncState>({
    wasAuthenticated: isAuthenticated,
    identifiedId: undefined,
  });

  useEffect(() => {
    syncStateRef.current = runPostHogSync(
      projectToken,
      isAuthenticated,
      homeData,
      posthog,
      syncStateRef.current
    );
  }, [isAuthenticated, homeData]);

  if (!projectToken) {
    return (children ?? null) as ReactElement | null;
  }

  return (
    <PostHogProviderReact client={posthog}>
      {children}
    </PostHogProviderReact>
  );
};
